import { defineStore } from 'pinia';

/** In-app notification item (see wiki docu linked below) */
interface HubNotification {
  /** Unique name identifying the notification (used for dedupe/removal) */
  name: string;
  /** Optional click handler; gets wrapped so the notification is removed first */
  action?: () => void;
  /** If true, notification stays until removed manually and sorts to the back */
  permanent?: boolean;
  /** Auto-dismiss delay in ms (default 3000) when not permanent and no action */
  timeout?: number;
  /** If true, once closed the notification is never shown again this session */
  stayDismissed?: boolean;
  /** Only show while on this route name */
  routeName?: string;
  /** Only show while on this exact route full path */
  routeFullPath?: string;
  /** Additional display fields (message, color, ...) used by the UI */
  [key: string]: any;
}

interface NotificationsState {
  /** Currently visible notifications, ordered (non-permanent before permanent) */
  notifications: HubNotification[];
  /** Names of stayDismissed notifications that were closed by the user */
  closedNotificationNames: string[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/notifications.ts` (namespace
 * `notifications`). No name collisions here: `addNotification` was already the only action, and
 * `setNotification`/`removeNotification`/`clearNotifications`/`clearRouteNotifications` were the
 * only mutations, so all five simply become same-named actions. See
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`.
 *
 * Persisted to `localStorage` (full state) — matches the legacy `vuexLocal` reducer's
 * `notifications: state.notifications` entry in `src/store/index.ts`.
 */
export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    notifications: [],
    closedNotificationNames: [],
  }),
  getters: {
    notification: (state): HubNotification | null => state.notifications[0] || null,
  },
  actions: {
    // Read the docu: https://github.com/knowhereto/moin-hub/wiki/Notifications
    // Don't forget to update the docu if you change something.

    async addNotification(notification: HubNotification) {
      if (this.closedNotificationNames.includes(notification.name)) return;

      if (notification.action) {
        // build wrapper function for action
        const actionWrapper = (action: () => void) => {
          return () => {
            this.removeNotification(notification.name);
            action();
          };
        };
        notification.action = actionWrapper(notification.action);
      } else if (!notification.permanent) {
        const timeout = notification.timeout || 3000;
        setTimeout(() => {
          this.removeNotification(notification.name);
        }, timeout);
      }

      if (!this.notifications.find((i: HubNotification) => i.name === notification.name)) {
        this.setNotification(notification);
      }
    },

    setNotification(notification: HubNotification) {
      let index = this.notifications.length;
      // Sort in front of permanent notification
      if (!notification.permanent && this.notifications.find((n) => n.permanent)) {
        index = this.notifications.findIndex((n) => n.permanent) - 1;
      }
      this.notifications.splice(index, 0, notification);
    },
    removeNotification(name: string) {
      const n = this.notifications.find((item) => item.name === name);
      if (n?.stayDismissed) {
        this.closedNotificationNames.push(name);
      }
      const nIndex = this.notifications.findIndex((item) => item.name === name);
      if (nIndex >= 0) {
        this.notifications.splice(nIndex, 1);
      }
    },
    clearNotifications() {
      this.notifications = [];
    },
    clearRouteNotifications({
      routeName,
      routeFullPath,
    }: {
      routeName?: string;
      routeFullPath?: string;
    }) {
      this.notifications = this.notifications.filter((notification) => {
        if (notification.routeName && notification.routeName !== routeName) {
          return false;
        }
        if (notification.routeFullPath && notification.routeFullPath !== routeFullPath) {
          return false;
        }
        return true;
      });
    },
  },
  vuexPersist: {
    local: true,
  },
});
